"use client";

import Cookies from 'js-cookie';
import React, { ReactNode } from 'react';
import useSWR from 'swr';

import { IntegrationContext, IntegrationContextType, XeroIntegration, EmailIntegration, IntegrationSlugs } from './integration-context';

interface AvailableIntegration {
    slug: string;
    name: string;
}

interface ConfiguredIntegration {
    slug: string;
    integration_name: string;
    integration_slug: string;
    xero_tenants?: {
        tenantId: string;
        tenantName: string;
        tenantType: string;
        createdDateUtc: string;
        updatedDateUtc: string;
        fyEndMonth?: number;
    }[];
    xero_tenant_default?: {
        tenantId: string;
        tenantName: string;
        tenantType: string;
        createdDateUtc: string;
        updatedDateUtc: string;
        fyEndMonth?: number;
    };
    xero_tenant_selected?: {
        tenantId: string;
        tenantName: string;
        tenantType: string;
        createdDateUtc: string;
        updatedDateUtc: string;
        fyEndMonth?: number;
    };
    xero_tenant_locked?: boolean;
}

interface IntegrationProviderProps {
    children: ReactNode;
}

// SWR fetcher functions
const fetchAvailableIntegrations = async (url: string): Promise<AvailableIntegration[]> => {
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status}`);
    }

    return response.json();
};

const fetchConfiguredIntegrations = async (url: string): Promise<ConfiguredIntegration[]> => {
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status}`);
    }

    return response.json();
};

export const IntegrationProvider: React.FC<IntegrationProviderProps> = ({ children }) => {
    // Fetch all available integrations
    const { data: availableIntegrations = [], error: availableError, isLoading: availableLoading } = useSWR<AvailableIntegration[]>(
        '/api/integrations/',
        fetchAvailableIntegrations,
        {
            revalidateOnFocus: true,
            revalidateOnReconnect: true,
            dedupingInterval: 0,
            errorRetryCount: 3,
            refreshInterval: 60000, // Refresh every minute
        }
    );

    // Fetch user's configured integrations
    const { data: configuredIntegrations = [], error: configuredError, isLoading: configuredLoading, mutate } = useSWR<ConfiguredIntegration[]>(
        '/api/integrations/connected/',
        fetchConfiguredIntegrations,
        {
            revalidateOnFocus: true,
            revalidateOnReconnect: true,
            dedupingInterval: 0,
            errorRetryCount: 3,
            refreshInterval: 60000, // Refresh every minute
        }
    );

    // Combine the data to show connection status
    const integrations: (XeroIntegration | EmailIntegration)[] = availableIntegrations.map((available) => {
        const configuredIntegration = configuredIntegrations.find(
            (configured) => configured.integration_slug === available.slug
        );
        const isConnected = !!configuredIntegration;

        const baseIntegration = {
            slug: available.slug,
            name: available.name,
            connected: isConnected,
        };

        if (available.slug === 'xero' && configuredIntegration?.xero_tenants && configuredIntegration?.xero_tenant_default) {
            return {
                ...baseIntegration,
                xeroTenants: configuredIntegration.xero_tenants,
                xeroTenantDefault: configuredIntegration.xero_tenant_default,
                xeroTenantSelected: configuredIntegration.xero_tenant_selected || configuredIntegration.xero_tenant_default,
                xeroTenantLocked: configuredIntegration.xero_tenant_locked || false,
            } as XeroIntegration;
        }

        return baseIntegration as EmailIntegration;
    });

    function getIntegration<T extends IntegrationSlugs>(slug: T): T extends 'xero' ? XeroIntegration | undefined : T extends 'email' ? EmailIntegration | undefined : never;
    function getIntegration(slug: string): undefined;
    function getIntegration(slug: string) {
        return integrations.find(integration => integration.slug === slug);
    }

    const refreshIntegrations = () => {
        mutate();
    };

    const setXeroTenant = async (tenantId: string) => {
        const csrfToken = Cookies.get('csrftoken') || '';
        const response = await fetch('/api/integrations/xero/tenant/', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
            },
            credentials: 'include',
            body: JSON.stringify({tenant_id: tenantId}),
        });
        if (!response.ok) {
            let message = `Failed to set Xero tenant (${response.status})`;
            try {
                const json = await response.json();
                if (json?.error) {
                    message = json.error;
                }
            } catch {
                // ignore parse failures
            }
            throw new Error(message);
        }
        await mutate();
    };

    const lockXeroTenant = async () => {
        const csrfToken = Cookies.get('csrftoken') || '';
        const response = await fetch('/api/integrations/xero/tenant/lock/', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
            },
            credentials: 'include',
        });
        if (!response.ok) {
            throw new Error(`Failed to lock tenant (${response.status})`);
        }
        await mutate();
    };

    const unlockXeroTenant = async () => {
        const csrfToken = Cookies.get('csrftoken') || '';
        const response = await fetch('/api/integrations/xero/tenant/lock/', {
            method: 'DELETE',
            headers: {
                'X-CSRFToken': csrfToken,
            },
            credentials: 'include',
        });
        if (!response.ok) {
            throw new Error(`Failed to unlock tenant (${response.status})`);
        }
        await mutate();
    };

    const isLoading = availableLoading || configuredLoading;
    const error = availableError || configuredError;

    const value: IntegrationContextType = {
        integrations,
        loading: isLoading,
        error: error ? (error instanceof Error ? error.message : 'Failed to fetch integrations') : null,
        getIntegration,
        refreshIntegrations,
        setXeroTenant,
        lockXeroTenant,
        unlockXeroTenant,
    };

    return (
        <IntegrationContext.Provider value={value}>
            {children}
        </IntegrationContext.Provider>
    );
};
